import type { TournamentMatch, TournamentRegistration } from "@mixitone/models";
import { isNil } from "@mixitone/util";

export function getMatchEntryIds(match: TournamentMatch): string[] {
  return match.entries?.current?.filter((entryId): entryId is string => typeof entryId === "string") || [];
}

export function buildEntryIdToPlayerIds(
  registrations: Iterable<TournamentRegistration>,
): Map<string, string[]> {
  const map = new Map<string, string[]>();
  for (const registration of registrations) {
    if (!registration.tournament_entry_id || !registration.tournament_player_id) continue;
    const playerIds = map.get(registration.tournament_entry_id) ?? [];
    playerIds.push(registration.tournament_player_id);
    map.set(registration.tournament_entry_id, playerIds);
  }
  return map;
}

export function getCourtOccupiedEntryIds(
  matches: Iterable<TournamentMatch>,
  options: { excludeMatchId?: string } = {},
): Set<string> {
  const occupiedEntryIds = new Set<string>();
  for (const match of matches) {
    if (options.excludeMatchId && match.id === options.excludeMatchId) continue;
    if (!(match.isReady || match.isInProgress) || isNil(match.court_id)) continue;
    for (const entryId of getMatchEntryIds(match)) {
      occupiedEntryIds.add(entryId);
    }
  }
  return occupiedEntryIds;
}

/** Player IDs currently on a court (ready or in progress), session-wide. */
export function getCourtOccupiedPlayerIds(
  matches: Iterable<TournamentMatch>,
  entryIdToPlayerIds: Map<string, string[]>,
  options: { excludeMatchId?: string } = {},
): Set<string> {
  const occupiedPlayerIds = new Set<string>();
  for (const entryId of getCourtOccupiedEntryIds(matches, options)) {
    for (const playerId of entryIdToPlayerIds.get(entryId) ?? []) {
      occupiedPlayerIds.add(playerId);
    }
  }
  return occupiedPlayerIds;
}

export interface RestingPlayerInfo {
  playerId: string;
  lastCompletedAt: Date;
  remainingMinutes: number;
}

export function derivePlayerLastCompletedAt(params: {
  matches: Iterable<TournamentMatch>;
  entryIdToPlayerIds: Map<string, string[]>;
}): Map<string, Date> {
  const { matches, entryIdToPlayerIds } = params;
  const playerLastCompleted = new Map<string, Date>();

  for (const match of matches) {
    if (match.status !== "completed" && !match.isComplete) continue;
    const completedAt = match.completed_at;
    if (!completedAt) continue;

    for (const entryId of getMatchEntryIds(match)) {
      const playerIds = entryIdToPlayerIds.get(entryId) ?? [];
      for (const playerId of playerIds) {
        const existing = playerLastCompleted.get(playerId);
        if (!existing || completedAt > existing) {
          playerLastCompleted.set(playerId, completedAt);
        }
      }
    }
  }
  return playerLastCompleted;
}

export function getRestingPlayers(params: {
  playerLastCompletedAt: Map<string, Date>;
  standDownMinutes: number;
  now: Date;
}): RestingPlayerInfo[] {
  const { playerLastCompletedAt, standDownMinutes, now } = params;
  if (standDownMinutes <= 0) return [];

  const resting: RestingPlayerInfo[] = [];
  const standDownMs = standDownMinutes * 60 * 1000;

  for (const [playerId, lastCompletedAt] of playerLastCompletedAt) {
    const restUntil = lastCompletedAt.getTime() + standDownMs;
    if (restUntil > now.getTime()) {
      const remainingMinutes = Math.ceil((restUntil - now.getTime()) / 60_000);
      resting.push({ playerId, lastCompletedAt, remainingMinutes });
    }
  }
  return resting;
}

export interface MatchAssignmentEligibility {
  eligible: boolean;
  occupiedEntryIds: Set<string>;
  restingPlayerIds: Set<string>;
  restingPlayersOnMatch: RestingPlayerInfo[];
  uncheckedEntryIds: Set<string>;
}

export function getMatchAssignmentEligibility(params: {
  match: TournamentMatch;
  matches: Iterable<TournamentMatch>;
  entryIdToPlayerIds: Map<string, string[]>;
  standDownMinutes: number;
  now: Date;
  occupiedEntryIds?: Set<string>;
  excludeMatchId?: string;
  /** Entry IDs that are not fully checked in; any presence on the match blocks assignment. */
  uncheckedEntryIds?: Set<string>;
}): MatchAssignmentEligibility {
  const { match, matches, entryIdToPlayerIds, standDownMinutes, now, excludeMatchId } = params;
  const matchList = [...matches];

  if (match.isVoided || match.status === "voided") {
    return {
      eligible: false,
      occupiedEntryIds: new Set(),
      restingPlayerIds: new Set(),
      restingPlayersOnMatch: [],
      uncheckedEntryIds: new Set(),
    };
  }

  const occupiedEntryIds = params.occupiedEntryIds ?? getCourtOccupiedEntryIds(matchList, { excludeMatchId });
  const occupiedPlayerIds = getCourtOccupiedPlayerIds(matchList, entryIdToPlayerIds, { excludeMatchId });

  const playerLastCompleted = derivePlayerLastCompletedAt({ matches: matchList, entryIdToPlayerIds });
  const restingPlayers = getRestingPlayers({
    playerLastCompletedAt: playerLastCompleted,
    standDownMinutes,
    now,
  });
  const restingPlayerIds = new Set(restingPlayers.map((player) => player.playerId));

  const entryIds = getMatchEntryIds(match);
  const occupiedOnMatch = entryIds.filter((entryId) => {
    if (occupiedEntryIds.has(entryId)) return true;
    const playerIds = entryIdToPlayerIds.get(entryId) ?? [];
    return playerIds.some((playerId) => occupiedPlayerIds.has(playerId));
  });

  const restingPlayersOnMatch: RestingPlayerInfo[] = [];
  for (const entryId of entryIds) {
    const playerIds = entryIdToPlayerIds.get(entryId) ?? [];
    for (const playerId of playerIds) {
      if (!restingPlayerIds.has(playerId)) continue;
      const info = restingPlayers.find((player) => player.playerId === playerId);
      if (info && !restingPlayersOnMatch.some((player) => player.playerId === playerId)) {
        restingPlayersOnMatch.push(info);
      }
    }
  }

  const uncheckedOnMatch = params.uncheckedEntryIds
    ? entryIds.filter((entryId) => params.uncheckedEntryIds!.has(entryId))
    : [];

  const eligible =
    occupiedOnMatch.length === 0 && restingPlayersOnMatch.length === 0 && uncheckedOnMatch.length === 0;

  return {
    eligible,
    occupiedEntryIds: new Set(occupiedOnMatch),
    restingPlayerIds,
    restingPlayersOnMatch,
    uncheckedEntryIds: new Set(uncheckedOnMatch),
  };
}

export function isMatchEligibleForCourtAssignment(params: {
  match: TournamentMatch;
  matches: Iterable<TournamentMatch>;
  entryIdToPlayerIds: Map<string, string[]>;
  standDownMinutes: number;
  now: Date;
  occupiedEntryIds?: Set<string>;
  excludeMatchId?: string;
  uncheckedEntryIds?: Set<string>;
}): boolean {
  return getMatchAssignmentEligibility(params).eligible;
}

export type AssignmentBlockReason = "occupied" | "rest" | "unchecked" | "both" | "none";

export function summarizeAssignmentBlock(params: {
  eligibility: MatchAssignmentEligibility;
}): AssignmentBlockReason {
  const hasOccupied = params.eligibility.occupiedEntryIds.size > 0;
  const hasRest = params.eligibility.restingPlayersOnMatch.length > 0;
  const hasUnchecked = params.eligibility.uncheckedEntryIds.size > 0;
  if (hasUnchecked && !hasOccupied && !hasRest) return "unchecked";
  if (hasOccupied && hasRest) return "both";
  if (hasOccupied) return "occupied";
  if (hasRest) return "rest";
  if (hasUnchecked) return "unchecked";
  return "none";
}

export function formatNoAssignableMatchMessage(params: {
  ineligibleReasons: {
    notReady: number;
    alreadyAssigned: number;
    inactiveCompetition: number;
    occupiedEntries: number;
    restingPlayers: number;
    uncheckedEntries?: number;
  };
  standDownMinutes: number;
  maxRestingMinutes?: number;
}): string {
  const { ineligibleReasons, standDownMinutes, maxRestingMinutes } = params;

  const blocks: string[] = [];
  if ((ineligibleReasons.uncheckedEntries ?? 0) > 0) {
    blocks.push("some players are not checked in");
  }
  if (ineligibleReasons.occupiedEntries > 0) {
    blocks.push("players are on other courts");
  }
  if (ineligibleReasons.restingPlayers > 0 && standDownMinutes > 0) {
    if (maxRestingMinutes !== undefined && maxRestingMinutes > 0) {
      blocks.push(
        `players need up to ${maxRestingMinutes} more minute${maxRestingMinutes === 1 ? "" : "s"} rest (${standDownMinutes}-minute stand-down)`,
      );
    } else {
      blocks.push(`players need more rest time (${standDownMinutes}-minute stand-down)`);
    }
  }

  if (blocks.length > 0) {
    const reason = blocks.join(" and ");
    return `No suitable matches available: ${reason}. Playoff and next matches will wait until players are free.`;
  }

  if (ineligibleReasons.inactiveCompetition > 0) {
    return "No suitable matches available in active competitions.";
  }

  return "No suitable matches available.";
}

export function formatMatchAssignmentBlockedMessage(params: {
  eligibility: MatchAssignmentEligibility;
  standDownMinutes: number;
}): string {
  const reason = summarizeAssignmentBlock({ eligibility: params.eligibility });
  const maxRest = Math.max(
    0,
    ...params.eligibility.restingPlayersOnMatch.map((player) => player.remainingMinutes),
  );

  if (reason === "unchecked") {
    return "Cannot assign court: one or more players in this match are not checked in.";
  }
  if (reason === "both") {
    return `Cannot assign court: players are on other courts and need up to ${maxRest} more minute${maxRest === 1 ? "" : "s"} rest (${params.standDownMinutes}-minute stand-down).`;
  }
  if (reason === "occupied") {
    return "Cannot assign court: one or more players are already on another court.";
  }
  if (reason === "rest") {
    return `Cannot assign court: players need up to ${maxRest} more minute${maxRest === 1 ? "" : "s"} rest (${params.standDownMinutes}-minute stand-down). Playoff matches will wait until stand-down elapses.`;
  }
  return "No suitable matches available.";
}

/** User-facing message when Start is blocked after a late eligibility change. */
export function formatMatchStartBlockedMessage(params: {
  eligibility: MatchAssignmentEligibility;
  standDownMinutes: number;
}): string {
  const reason = summarizeAssignmentBlock({ eligibility: params.eligibility });
  const maxRest = Math.max(
    0,
    ...params.eligibility.restingPlayersOnMatch.map((player) => player.remainingMinutes),
  );

  if (reason === "unchecked") {
    return "Cannot start match: one or more players in this match are not checked in.";
  }
  if (reason === "both") {
    return `Cannot start match: players are on other courts and need up to ${maxRest} more minute${maxRest === 1 ? "" : "s"} rest (${params.standDownMinutes}-minute stand-down).`;
  }
  if (reason === "occupied") {
    return "Cannot start match: one or more players are already on another court.";
  }
  if (reason === "rest") {
    return `Cannot start match: players need up to ${maxRest} more minute${maxRest === 1 ? "" : "s"} rest (${params.standDownMinutes}-minute stand-down).`;
  }
  return "Cannot start match.";
}

export interface AssignedMatchStartEvaluation {
  canStart: boolean;
  /** When start is blocked by unchecked players, the caller should free the court. */
  shouldUnassign: boolean;
  message?: string;
  eligibility: MatchAssignmentEligibility;
}

/**
 * Re-check eligibility before starting an already-assigned match.
 * Excludes this match from occupancy so its own court does not block start.
 */
export function evaluateAssignedMatchStart(params: {
  match: TournamentMatch;
  matches: Iterable<TournamentMatch>;
  entryIdToPlayerIds: Map<string, string[]>;
  standDownMinutes: number;
  now: Date;
  uncheckedEntryIds?: Set<string>;
  occupiedEntryIds?: Set<string>;
}): AssignedMatchStartEvaluation {
  const eligibility = getMatchAssignmentEligibility({
    match: params.match,
    matches: params.matches,
    entryIdToPlayerIds: params.entryIdToPlayerIds,
    standDownMinutes: params.standDownMinutes,
    now: params.now,
    occupiedEntryIds: params.occupiedEntryIds,
    excludeMatchId: params.match.id,
    uncheckedEntryIds: params.uncheckedEntryIds,
  });

  if (eligibility.eligible) {
    return { canStart: true, shouldUnassign: false, eligibility };
  }

  return {
    canStart: false,
    shouldUnassign: eligibility.uncheckedEntryIds.size > 0,
    message: formatMatchStartBlockedMessage({
      eligibility,
      standDownMinutes: params.standDownMinutes,
    }),
    eligibility,
  };
}
